import m from "mithril";
import type { Item, ParseResult, Phase, Region } from "./types.js";

/** The one place mutable app state lives. Components read from `store` and call
 *  its methods; anything async calls `m.redraw()` so the UI catches up. */
class Store {
  phase: Phase = "idle";
  status = "";

  /** The image the user loaded, as an object URL and a bitmap source. */
  imageUrl: string | null = null;
  imageEl: HTMLImageElement | null = null;

  items: Item[] = [];
  detectedTotal: number | null = null;
  currency = "€";
  parseMethod: ParseResult["method"] | null = null;

  /** The last raw OCR text, shown in the "raw scan" panel for debugging. */
  ocrText = "";

  /** Optional region the user drew to constrain the next scan. */
  region: Region | null = null;

  /** Final-bill inputs for the tip-per-head calculation. */
  finalBill: number | null = null;
  people = 1;

  setPhase(phase: Phase, status = "") {
    this.phase = phase;
    this.status = status;
    m.redraw();
  }

  setStatus(status: string) {
    this.status = status;
    m.redraw();
  }

  async loadImage(blob: Blob) {
    if (this.imageUrl) URL.revokeObjectURL(this.imageUrl);
    this.imageUrl = URL.createObjectURL(blob);
    this.imageEl = await loadImageEl(this.imageUrl);
    this.items = [];
    this.detectedTotal = null;
    this.parseMethod = null;
    this.ocrText = "";
    this.region = null;
    this.setPhase("loaded", "Photo loaded. Scan it, or draw a box first.");
  }

  applyParse(result: ParseResult) {
    this.items = result.items;
    this.detectedTotal = result.detectedTotal;
    this.currency = result.currency;
    this.parseMethod = result.method;
    this.setPhase("ready");
  }

  addBlankItem() {
    this.items.push({
      id: `item-${Date.now()}-${Math.floor(Math.random() * 1e6)}`,
      name: "",
      qty: null,
      unitPrice: null,
      lineTotal: null,
      selected: true,
      source: "",
    });
  }

  removeItem(id: string) {
    this.items = this.items.filter((it) => it.id !== id);
  }

  /** Total for a single line: explicit lineTotal wins, else qty * unitPrice. */
  lineValue(it: Item): number {
    if (it.lineTotal != null) return it.lineTotal;
    if (it.unitPrice != null) return (it.qty ?? 1) * it.unitPrice;
    return 0;
  }

  /** Sum of the ticked items. */
  selectedTotal(): number {
    return this.items
      .filter((it) => it.selected)
      .reduce((sum, it) => sum + this.lineValue(it), 0);
  }

  /** Sum of every item, ticked or not — the receipt's implied total. */
  itemsTotal(): number {
    return this.items.reduce((sum, it) => sum + this.lineValue(it), 0);
  }

  /** Tip inferred from the final bill vs. the receipt total. Null if not enough
   *  info. Uses the detected printed total when available, else the items sum. */
  tip(): number | null {
    if (this.finalBill == null) return null;
    const base = this.detectedTotal ?? this.itemsTotal();
    if (base <= 0) return null;
    return this.finalBill - base;
  }

  tipPerHead(): number | null {
    const tip = this.tip();
    if (tip == null) return null;
    const heads = Math.max(1, this.people || 1);
    return tip / heads;
  }

  /** What you owe: your ticked items plus one head's share of the tip. */
  yourTotal(): number {
    const tipHead = this.tipPerHead();
    return this.selectedTotal() + (tipHead && tipHead > 0 ? tipHead : 0);
  }
}

function loadImageEl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

export const store = new Store();
